import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

export class ProductBundleElop extends Component {

  connectedCallback() {
    super.connectedCallback();

    this.carousel = this.querySelector(".elop-bundle__carousel");
    this.navigation = this.querySelector('.elop-bundle__navigation');

    this.resizeObserver = new ResizeObserver(() => {
      this.#updateNavigation();
    });
    this.resizeObserver.observe(this.carousel);
    this.#updateNavigation();

    const add_all_button = this.querySelector(".elop-bundle__button");
    add_all_button.addEventListener('click', () => this.#processBatchAddToCart());

    const navigation_previous = this.querySelector(".elop-bundle__navigation-previous");
    navigation_previous.addEventListener('click', () => this.#navigatePrevious());

    const navigation_next = this.querySelector(".elop-bundle__navigation-next");
    navigation_next.addEventListener('click', () => this.#navigateNext());

    this.querySelectorAll(".elop-bundle__product-card").forEach((card) => {
      this.setupCard(card);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.resizeObserver?.disconnect();

    const add_all_button = this.querySelector(".elop-bundle__button");
    add_all_button.removeEventListener('click', () => this.#processBatchAddToCart());

    const navigation_previous = this.querySelector(".elop-bundle__navigation-previous");
    navigation_previous.removeEventListener('click', () => this.#navigatePrevious());

    const navigation_next = this.querySelector(".elop-bundle__navigation-next");
    navigation_next.removeEventListener('click', () => this.#navigateNext());
  }

  setupCard(card) {
    const data = card.querySelector(".elop-bundle__variants-data");

    if (!data) return;

    const variants = JSON.parse(data.textContent);

    card.querySelectorAll("select").forEach((select) => {
      select.addEventListener("change", () => {
        this.updateVariant(card, variants);
      });
    });
  }

  updateVariant(card, variants) {
    const selectedOptions = [];

    card.querySelectorAll("select").forEach((select) => {
      const position = Number(select.dataset.optionPosition) - 1;

      selectedOptions[position] = select.value;
    });

    const variant = variants.find((variant) => {
      return variant.options.every((option, index) => {
        return option === selectedOptions[index];
      });
    });

    if (!variant) return;
    
    card.dataset.variantId = variant.id;
    card.dataset.variantPrice = variant.price;
    card.dataset.variantCompareAtPrice = variant.compare_at_price || "";

    card.querySelector(".elop-bundle__price").textContent = formatMoney(
      variant.price,
      window.theme.moneyFormat,
      window.Shopify.currency.active
    );

    const comparePrice = card.querySelector(
      ".elop-bundle__price-compare"
    );

    if (comparePrice)
      comparePrice.textContent = 
        variant.compare_at_price 
        ? formatMoney(
            variant.compare_at_price,
            window.theme.moneyFormat,
            window.Shopify.currency.active
          ) : "";
  }

  #updateNavigation() {
    const hasOverflow = this.carousel.scrollWidth > this.carousel.clientWidth;
    this.navigation.hidden = !hasOverflow;
  }

  #navigatePrevious() {
    this.carousel.scrollBy({
      left: -100,
      behavior: "smooth"
    });
  }

  #navigateNext() {
    this.carousel.scrollBy({
      left: 100,
      behavior: "smooth"
    });
  }

  async #refreshCart() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    // Fallback for pages without cart-items-component (e.g. page-based cart on product pages)
    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  #processBatchAddToCart() {
    const items = [...this.querySelectorAll('[data-variant-id]')].map((card) => ({ variantId: Number(card.dataset.variantId), quantity: 1 }));
    if (items.length === 0) return;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: item.variantId,
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: {
                description: response.description,
                errors: response.errors,
              },
            })
          );

          this.#refreshCart()
            .then((ajaxCart) =>
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  didError: true,
                  items: ajaxCart.items,
                  source: 'product-bundle-elop',
                  sourceId: this.id.toString(),
                  itemCount: totalQuantity,
                  productId: this.dataset.productId,
                },
              })
            )
            .catch(deferredEventPromise.reject);

          return;
        }

        const cart = await this.#refreshCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'product-bundle-elop',
            sourceId: this.id.toString(),
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
            didError: false,
          },
        });
      })
      .catch((error) => {
        console.error(error);
        deferredEventPromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      });
  }
}

if (!customElements.get('product-bundle-elop')) {
  customElements.define('product-bundle-elop', ProductBundleElop);
}